import { css } from '@emotion/react';
import type { Channel } from '@weave/schema-gen-ts/dist/schemas/integrations/sync-config-api/v1/message.pb';
import dayjs from 'dayjs';
import { Button, SpinningLoader, Text } from '@frontend/design-system';

interface ChannelActionProps {
  targetChannelName?: string;
  onPromoteClick?: (channel: Channel) => void;
  onReplaceClick?: (channel: Channel) => void;
  isManageDisabled?: boolean;
}

interface ChannelColumnProps extends ChannelActionProps {
  channelName: string;
  entries: Channel[];
  isReplacingTrain?: boolean;
}

export const ChannelColumn = ({
  channelName,
  entries,
  targetChannelName,
  onPromoteClick,
  onReplaceClick,
  isManageDisabled,
  isReplacingTrain,
}: ChannelColumnProps) => {
  // Channels with no downstream promotion target render with "terminal" (stable-style) coloring.
  const isTerminal = !targetChannelName;

  return (
    <section css={[columnBaseStyle, isTerminal ? columnTerminalAccent : columnDefaultAccent]}>
      <header css={headerStyle}>
        <Text weight='bold' size='large' color={isTerminal ? 'success' : 'primary'}>
          {channelName.toUpperCase()}
        </Text>
      </header>

      <div css={entriesStyle}>
        {entries.map((entry, index) => (
          <div key={`${entry.currentTrainId}-${index}`} css={cardWrapperStyle}>
            <ChannelCard
              channel={entry}
              targetChannelName={targetChannelName}
              onPromoteClick={onPromoteClick}
              onReplaceClick={onReplaceClick}
              isManageDisabled={isManageDisabled}
            />
            {isReplacingTrain && index === 0 && (
              <div css={updatingOverlayStyle} role='status' aria-live='polite'>
                <SpinningLoader size='medium' />
                <Text size='small' weight='bold'>
                  Updating…
                </Text>
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

interface ChannelCardProps extends ChannelActionProps {
  channel: Channel;
}

const ChannelCard = ({
  channel,
  targetChannelName,
  onPromoteClick,
  onReplaceClick,
  isManageDisabled,
}: ChannelCardProps) => {
  const canPromote = Boolean(targetChannelName && onPromoteClick);
  const isTerminal = !targetChannelName;

  return (
    <div css={[cardBaseStyle, isTerminal ? cardTerminalAccent : cardDefaultAccent]}>
      {onReplaceClick && (
        <Button
          iconName='edit'
          hoverLabel={
            isManageDisabled ? 'You do not have enough permissions' : `Replace train on ${channel.channelName}`
          }
          disabled={isManageDisabled}
          showLabelWhenDisabled={isManageDisabled}
          onClick={() => onReplaceClick(channel)}
          variant='tertiary'
          size='small'
          css={editButtonStyle}
        />
      )}
      <Text css={trainIdStyle}>Train ID: {channel.currentTrainId}</Text>
      <Text size='small' css={metaStyle}>
        Updated At:{' '}
        {channel.updatedAt && dayjs(channel.updatedAt).isValid()
          ? dayjs(channel.updatedAt).format('DD MMM YYYY hh:mm A')
          : 'N/A'}
      </Text>
      <Text size='small' css={metaStyle}>
        Promoted By: {channel.channelPromotedBy}
      </Text>
      {canPromote && (
        <Button
          variant='primary'
          disabled={isManageDisabled}
          hoverLabel={isManageDisabled ? 'You do not have enough permissions' : undefined}
          showLabelWhenDisabled={isManageDisabled}
          onClick={() => onPromoteClick?.(channel)}
          css={promoteButtonStyle}
        >
          Promote {channel.channelName} to {targetChannelName}
        </Button>
      )}
    </div>
  );
};

const columnBaseStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--fabric-space-gap-md);
  padding: var(--fabric-space-padding-md);
  border: var(--fabric-border-width-thin) solid;
  border-radius: var(--fabric-radius-md);
  min-width: 260px;
`;

const columnDefaultAccent = css`
  background-color: var(--fabric-color-blue-5);
  border-color: var(--fabric-color-blue-20);
`;

const columnTerminalAccent = css`
  background-color: var(--fabric-color-green-5);
  border-color: var(--fabric-color-green-20);
`;

const headerStyle = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--fabric-space-gap-md);
  padding-bottom: var(--fabric-space-padding-xs);
  border-bottom: var(--fabric-border-width-thin) solid var(--fabric-color-neutral-20);
`;

const entriesStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--fabric-space-gap-sm);
`;

const cardWrapperStyle = css`
  position: relative;
`;

const updatingOverlayStyle = css`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--fabric-space-gap-xs);
  background-color: var(--fabric-color-surface-overlay-light);
  border-radius: var(--fabric-radius-md);
  backdrop-filter: blur(var(--fabric-elevation-light-overlay-blur));
`;

const cardBaseStyle = css`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--fabric-space-gap-xs);
  padding: var(--fabric-space-padding-md);
  background-color: var(--fabric-color-neutral-00);
  border: var(--fabric-border-width-thin) solid var(--fabric-color-neutral-20);
  border-left-width: var(--fabric-number-n-1-px);
  border-left-style: solid;
  border-radius: var(--fabric-radius-md);
`;

const cardDefaultAccent = css`
  border-left-color: var(--fabric-color-blue-50);
`;

const cardTerminalAccent = css`
  border-left-color: var(--fabric-color-green-50);
`;

const editButtonStyle = css`
  position: absolute;
  top: var(--fabric-space-padding-xs);
  right: var(--fabric-space-padding-xs);
`;

const trainIdStyle = css`
  color: var(--fabric-color-neutral-90);
  word-break: break-all;
  padding-right: var(--fabric-space-gap-lg);
`;

const metaStyle = css`
  color: var(--fabric-color-neutral-50);
`;

const promoteButtonStyle = css`
  margin-top: var(--fabric-space-gap-xs);
`;
